import { Injectable, computed, inject, signal } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import Keycloak from 'keycloak-js';
import {
  ParkerService,
  MyParkingPaymentsService,
  PublicParkingProcessService,
  ParkerLicenseDto,
  PayableStayDto,
  PublicParkingProcessDto,
  ParkerSpaceEntitlementDto,
  RegisteredParkerDto
} from '../../api/gen';
import { environment } from '../../environments/environment';
import { normalizePlate } from 'shared-lib';

@Injectable({ providedIn: 'root' })
export class ParkerStateService {
  private readonly keycloak = inject(Keycloak);
  private readonly parkerService = inject(ParkerService);
  private readonly paymentsService = inject(MyParkingPaymentsService);
  private readonly publicProcessService = inject(PublicParkingProcessService);

  // State Signals
  readonly account = signal<RegisteredParkerDto | null>(null);
  readonly myPlates = signal<ParkerLicenseDto[]>([]);
  readonly payableStays = signal<PayableStayDto[]>([]);
  readonly activeSessions = signal<PublicParkingProcessDto[]>([]);
  readonly entitlements = signal<ParkerSpaceEntitlementDto[]>([]);

  // Loading Signals
  readonly isLoading = signal<boolean>(false);
  readonly isPayablesLoading = signal<boolean>(false);
  readonly isPlatesLoading = signal<boolean>(false);
  readonly isEntitlementsLoading = signal<boolean>(false);
  readonly isActiveSessionsLoading = signal<boolean>(false);

  // Computed Properties
  readonly isAuthenticated = computed(() => !!this.keycloak.authenticated);

  readonly unpaidAmount = computed(() =>
    this.payableStays().reduce((sum, stay) => sum + (stay.amountDue ?? 0), 0)
  );

  readonly unpaidCount = computed(() =>
    this.payableStays().filter(stay => (stay.amountDue ?? 0) > 0).length
  );

  readonly hasActiveSession = computed(() => this.activeSessions().length > 0);

  /**
   * Initializes or refreshes all data for an authenticated parker.
   */
  refreshAll(): void {
    if (!this.keycloak.authenticated) {
      this.clear();
      return;
    }

    this.isLoading.set(true);
    this.isPayablesLoading.set(true);
    this.isPlatesLoading.set(true);
    this.isEntitlementsLoading.set(true);
    this.isActiveSessionsLoading.set(true);

    // 1. Ensure/Fetch account
    this.parkerService.parkerMePost().pipe(
      catchError(err => {
        console.error('Error fetching parker account:', err);
        return of(null);
      })
    ).subscribe(acc => {
      if (acc) this.account.set(acc);
    });

    // 2. Fetch Plates, Payables, and Entitlements in parallel
    forkJoin({
      plates: this.parkerService.parkerMeLicensePlatesGet().pipe(catchError(() => of([] as ParkerLicenseDto[]))),
      payables: this.paymentsService.myParkingPayableStaysGet().pipe(catchError(() => of([] as PayableStayDto[]))),
      entitlements: this.parkerService.parkerMeParkingEntitlementsGet().pipe(catchError(() => of([] as ParkerSpaceEntitlementDto[])))
    }).subscribe({
      next: ({ plates, payables, entitlements }) => {
        this.myPlates.set(plates);
        this.payableStays.set(payables);
        this.entitlements.set(entitlements);
        this.isPayablesLoading.set(false);
        this.isPlatesLoading.set(false);
        this.isEntitlementsLoading.set(false);

        // Fetch active sessions for owned plates
        this.fetchActiveSessionsForPlates(plates);
      },
      error: () => {
        this.isLoading.set(false);
        this.isPayablesLoading.set(false);
        this.isPlatesLoading.set(false);
        this.isEntitlementsLoading.set(false);
        this.isActiveSessionsLoading.set(false);
      }
    });
  }

  /**
   * Queries active parking processes across all registered plates.
   */
  private fetchActiveSessionsForPlates(plates: ParkerLicenseDto[]): void {
    if (!plates || plates.length === 0) {
      this.activeSessions.set([]);
      this.isActiveSessionsLoading.set(false);
      this.isLoading.set(false);
      return;
    }

    const requests = plates.map(p => {
      const plateNumber = normalizePlate(p.licensePlate?.fullNumber);
      if (!plateNumber) return of(null);
      return this.publicProcessService.parkingProcessPublicByLicensePlatePost({
        licensePlateNumber: plateNumber,
        pageSize: 1,
        pageIndex: 0
      }).pipe(
        catchError(() => of(null))
      );
    });

    forkJoin(requests).subscribe({
      next: (results) => {
        const active: PublicParkingProcessDto[] = [];
        results.forEach(res => {
          if (res && res.items && res.items.length > 0) {
            const first = res.items[0];
            if (!first.endedAt) {
              active.push(first);
            }
          }
        });
        this.activeSessions.set(active);
        this.isActiveSessionsLoading.set(false);
        this.isLoading.set(false);
      },
      error: () => {
        this.isActiveSessionsLoading.set(false);
        this.isLoading.set(false);
      }
    });
  }

  /**
   * Reload only payable stays (e.g. after a payment or pull-to-refresh).
   */
  refreshPayableStays(): void {
    if (!this.keycloak.authenticated) return;
    this.isPayablesLoading.set(true);
    this.paymentsService.myParkingPayableStaysGet().pipe(
      catchError(() => of([] as PayableStayDto[]))
    ).subscribe(payables => {
      this.payableStays.set(payables);
      this.isPayablesLoading.set(false);
    });
  }

  /**
   * Builds the deep-link URL to pay for a parking process, or null if this build has no payment app
   * configured.
   *
   * Checkout lives in a separate application, so this is always a cross-origin link. It used to fall
   * back to `window.location.origin` when `paymentAppUrl` was unset — which reads as harmless and is
   * not: this app has no `/pay` route and no wildcard, so a parker who owed money tapped Pay and
   * landed on a screen the router could not match. `paymentAppUrl` was in fact empty in both the
   * testing and production builds, so that was every deployed environment.
   *
   * Returning null instead makes the missing configuration visible where it can be fixed, rather than
   * pointing the parker at ourselves and calling it a destination.
   */
  getPaymentUrl(parkingProcessId: string, returnUrl?: string): string | null {
    // Trailing slashes come from pasted URLs and would produce `…//pay/…`.
    const base = (environment.paymentAppUrl ?? '').replace(/\/+$/, '');
    if (!base) return null;

    const returnTarget = returnUrl || `${window.location.origin}/activity`;
    return `${base}/pay/${parkingProcessId}?returnUrl=${encodeURIComponent(returnTarget)}`;
  }

  /**
   * Navigates to the payment checkout for a parking process.
   *
   * Does nothing if there is nowhere to send them. Leaving the parker on a page that still shows what
   * they owe is a better failure than a dead screen: the debt is still visible and still payable at
   * the column or from the exit QR.
   */
  payStay(parkingProcessId?: string | null, returnUrl?: string): void {
    if (!parkingProcessId) return;

    const url = this.getPaymentUrl(parkingProcessId, returnUrl);
    if (!url) {
      console.error(
        'No paymentAppUrl configured for this build — cannot open checkout for stay',
        parkingProcessId
      );
      return;
    }

    window.location.href = url;
  }

  /**
   * Clears state when logged out.
   */
  clear(): void {
    this.account.set(null);
    this.myPlates.set([]);
    this.payableStays.set([]);
    this.activeSessions.set([]);
    this.entitlements.set([]);
    this.isLoading.set(false);
  }
}
